/**
 * The landing page and the anonymous intake handoff (merchant-intake).
 *
 * `/` renders the v3 landing page with the session-aware sign-in action and
 * the post-callback hop: the provider callback lands on `/` by design, so
 * when a sign-in was started in this tab, a fresh session routes onward to
 * the preserved destination (or onboarding, then the dashboard). Inside the
 * merchant workspace shell the same component is the "New Production Line"
 * page. Moved verbatim out of `App.tsx` in Refactor v2 Phase 7.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { Hero } from '../../components/Hero.js';
import { HeroNavigation } from '../../components/HeroNavigation.js';
import { LandingFooter } from '../../components/LandingFooter.js';
import { ProcessSection } from '../../components/ProcessSection.js';
import { ProductStorySection } from '../../components/ProductStorySection.js';
import { WaitlistSection } from '../../components/WaitlistSection.js';
import {
  VideoIntentComposer,
  type ComposerIntent,
} from '../../components/VideoIntentComposer.js';
import {
  contextSourcesAreComplete,
  storeUrlFrom,
  type ContextSource,
} from '../../lib/classifyContextUrl.js';
import { useSession } from '../../workspace/session.js';
import { LoadingPanel } from '../../workspace/pages/common.js';
import { usePendingSignInRedirect } from '../../workspace/pages/pending-sign-in.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { MerchantAuthModal } from './MerchantAuthModal.js';
import { MerchantProfilePage } from './MerchantProfilePage.js';
import { MerchantPublicHeader } from './MerchantPublicHeader.js';
import {
  useIsInsideMerchantWorkspaceShell,
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import type { WorkspaceLists } from '../../merchant/data.js';
import { missingProfileConfirmationFields } from '../../merchant/profileCompleteness.js';
import {
  approveMerchantProfile,
  claimMerchantIntake,
  createMerchantIntake,
  fetchMerchantIntake,
  MERCHANT_INTAKE_STORAGE_KEY,
  MERCHANT_PROFILE_INTENT_STORAGE_KEY,
  processMerchantIntake,
  type MerchantProfileView,
} from '../../merchant/api.js';

// Product Story stays available for local QA while it is deliberately hidden
// from deployed builds. Set the explicit flag only when testing a production
// build locally; staging should not enable it until the section is approved.
const productStoryEnabled =
  import.meta.env.DEV || import.meta.env['VITE_ENABLE_PRODUCT_STORY'] === 'true';

/** Scroll to an element by id, honouring the document's own scroll behaviour. */
function scrollToId(id: string): void {
  document.getElementById(id)?.scrollIntoView({ block: 'start' });
}

function profileIsConfirmable(view: MerchantProfileView): boolean {
  const profile = view.profile?.payload;
  return Boolean(
    profile?.identity.value?.trim() &&
    profile.niche.value?.trim() &&
    profile.geography.value?.trim() &&
    profile.offers.some((offer) => offer.title.value?.trim()),
  );
}

export function LandingPage(): ReactNode {
  const { copy, language } = useLanguage();
  const { session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [contextNote, setContextNote] = useState('');
  const [sources, setSources] = useState<readonly ContextSource[]>([]);
  const [intakeError, setIntakeError] = useState<string | null>(null);
  const [intakeStarting, setIntakeStarting] = useState(false);
  const [intakeId, setIntakeId] = useState<string | null>(null);
  const [intakeView, setIntakeView] = useState<MerchantProfileView | null>(null);
  const [profileActionBusy, setProfileActionBusy] = useState(false);
  const [authIntent, setAuthIntent] = useState<'confirm' | 'correct' | null>(null);
  const insideMerchantWorkspace = useIsInsideMerchantWorkspaceShell();
  const { setBusinessName } = useMerchantWorkspaceIdentity();

  useEffect(() => {
    if (intakeView?.business.displayName !== undefined) {
      setBusinessName(intakeView.business.displayName);
    }
  }, [intakeView?.business.displayName, setBusinessName]);

  // A sign-in that started in this tab finishes here: see workspace/pages/pending-sign-in.ts.
  usePendingSignInRedirect();

  const addSource = useCallback((source: ContextSource) => {
    setSources((all) =>
      all.some(
        (current) => current.id === source.id || current.platform === source.platform,
      )
        ? all
        : [...all, source],
    );
    setIntakeError(null);
  }, []);

  const removeSource = useCallback((id: string) => {
    setSources((all) => all.filter((source) => source.id !== id));
    setIntakeError(null);
  }, []);

  const goToTop = useCallback(() => {
    scrollToId('top');
  }, []);

  const storeUrl = useMemo(() => storeUrlFrom(sources), [sources]);

  const authenticated = session.status === 'authenticated';
  const merchant = authenticated && session.account.role === 'MERCHANT';

  useEffect(() => {
    if (intakeId === null || intakeView?.status !== 'PROCESSING') return undefined;
    const poll = () => {
      void fetchMerchantIntake(intakeId)
        .then((next) => {
          setIntakeView(next);
          setIntakeError(null);
        })
        .catch((error: unknown) => {
          setIntakeError(
            error instanceof Error ? error.message : copy.composer.progressUnavailable,
          );
        });
    };
    const timer = window.setInterval(poll, 1_500);
    return () => {
      window.clearInterval(timer);
    };
  }, [copy.composer.progressUnavailable, intakeId, intakeView?.status]);

  const startProfile = useCallback(
    async (intent: ComposerIntent) => {
      setContextNote(intent.contextNote);
      setSources(intent.sources);
      if (!contextSourcesAreComplete(intent.sources)) {
        setIntakeError(copy.composer.promptNeedsProduct);
        return;
      }
      const pilotToken = new URLSearchParams(location.search).get('pilot');
      setIntakeStarting(true);
      setIntakeView(null);
      setIntakeError(null);
      let createdId: string | null = null;
      try {
        const created = await createMerchantIntake({
          ...(pilotToken === null || pilotToken.length === 0 ? {} : { pilotToken }),
          contextNote: intent.contextNote,
          // The interface language at submission is the analysis language.
          outputLocale: language,
          sources: intent.sources.map((source) => ({
            url: source.url,
            platform: source.platform,
            ownership: 'mine' as const,
          })),
        });
        createdId = created.id;
        setIntakeId(created.id);
        try {
          localStorage.setItem(MERCHANT_INTAKE_STORAGE_KEY, created.id);
        } catch {
          setIntakeError(copy.composer.storageRequired);
          return;
        }
        const processed = await processMerchantIntake(created.id);
        setIntakeView(processed);
      } catch (error) {
        setIntakeError(
          error instanceof Error ? error.message : copy.composer.generationFailed,
        );
        if (createdId !== null) {
          void fetchMerchantIntake(createdId)
            .then(setIntakeView)
            .catch(() => undefined);
        }
      } finally {
        setIntakeStarting(false);
      }
    },
    [copy.composer, location.search, merchant],
  );

  const composerPhase = useMemo(() => {
    if (
      intakeStarting ||
      intakeView?.status === 'COLLECTING' ||
      intakeView?.status === 'PROCESSING'
    ) {
      return 'processing' as const;
    }
    if (intakeView !== null) {
      if (intakeView.profileIssue.state !== 'none' || intakeView.profile === null) {
        return 'insufficient' as const;
      }
      return 'profile' as const;
    }
    return 'input' as const;
  }, [intakeStarting, intakeView]);

  const resetProfile = useCallback(() => {
    setIntakeId(null);
    setIntakeView(null);
    setIntakeStarting(false);
    setIntakeError(null);
  }, []);

  const openMerchantAuth = useCallback((intent: 'confirm' | 'correct') => {
    try {
      sessionStorage.setItem(MERCHANT_PROFILE_INTENT_STORAGE_KEY, intent);
    } catch {
      // The profile route remains a complete recovery surface without this hint.
    }
    setAuthIntent(intent);
  }, []);

  const actOnProfile = useCallback(
    async (intent: 'confirm' | 'correct') => {
      const currentView = intakeView;
      if (intakeId === null || currentView === null || currentView.profile === null) {
        return;
      }
      if (!merchant) {
        if (authenticated) {
          setIntakeError(copy.composer.merchantAccountRequired);
          return;
        }
        openMerchantAuth(intent);
        return;
      }
      setProfileActionBusy(true);
      setIntakeError(null);
      try {
        const claimed = currentView.claimed
          ? currentView
          : await claimMerchantIntake(intakeId);
        setIntakeView(claimed);
        if (intent === 'correct') {
          void navigate('/business-profile');
          return;
        }
        if (!profileIsConfirmable(claimed)) {
          setIntakeError(copy.composer.completionRequired);
          return;
        }
        const claimedProfile = claimed.profile;
        if (claimedProfile === null) {
          setIntakeError(copy.composer.profileUnavailable);
          return;
        }
        const missingFields = missingProfileConfirmationFields(claimedProfile.payload);
        if (missingFields.length > 0) {
          setIntakeError(
            `Complete your business profile first: add ${missingFields.join(', ')}.`,
          );
          return;
        }
        if (claimedProfile.reviewState !== 'APPROVED') {
          await approveMerchantProfile(
            intakeId,
            claimedProfile.artifactId,
            newIdempotencyKey(),
          );
        }
        void navigate(`/merchant/intakes/${encodeURIComponent(intakeId)}`);
      } catch (error) {
        setIntakeError(
          error instanceof Error ? error.message : copy.composer.profileActionFailed,
        );
      } finally {
        setProfileActionBusy(false);
      }
    },
    [
      authenticated,
      copy.composer,
      intakeId,
      intakeView,
      merchant,
      navigate,
      openMerchantAuth,
    ],
  );

  if (insideMerchantWorkspace) {
    return (
      <main className="mj-merchant-page mj-merchant-new-line">
        <header className="mj-merchant-page-heading">
          <div>
            <p className="mj-merchant-eyebrow">
              {language === 'ar' ? 'خطوط الإنتاج' : 'Production Lines'}
            </p>
            <h1>{language === 'ar' ? 'خط إنتاج جديد' : 'New Production Line'}</h1>
            <p>{copy.composer.linkHint}</p>
          </div>
        </header>
        <section className="mj-merchant-intake-card">
          <VideoIntentComposer
            contextNote={contextNote}
            onContextNoteChange={(nextContextNote) => {
              setContextNote(nextContextNote);
              setIntakeError(null);
            }}
            sources={sources}
            onAddSource={addSource}
            onRemoveSource={removeSource}
            onSubmit={(intent) => {
              if (!intakeStarting) void startProfile(intent);
            }}
            phase={composerPhase}
            profileView={intakeView}
            busy={intakeStarting || profileActionBusy}
            externalError={intakeError}
            onConfirmProfile={() => {
              void actOnProfile('confirm');
            }}
            onProfileIncorrect={() => {
              void actOnProfile('correct');
            }}
            onRetrySources={resetProfile}
          />
        </section>
      </main>
    );
  }

  return (
    <>
      <a className="mj-skip-link" href="#main-content">
        {copy.skipToContent}
      </a>

      <HeroNavigation
        onHome={goToTop}
        onHowItWorks={() => {
          scrollToId('how-it-works');
        }}
        secondaryAction={{
          label: merchant
            ? copy.nav.openWorkspace
            : authenticated
              ? copy.nav.dashboard
              : copy.nav.signIn,
          onSelect: () => {
            void navigate(
              merchant ? '/home' : authenticated ? '/dashboard' : '/signin',
            );
          },
        }}
      />

      <main id="main-content">
        <Hero
          contextNote={contextNote}
          onContextNoteChange={(nextContextNote) => {
            setContextNote(nextContextNote);
            setIntakeError(null);
          }}
          sources={sources}
          onAddSource={addSource}
          onRemoveSource={removeSource}
          onSubmit={(intent) => {
            if (!intakeStarting) void startProfile(intent);
          }}
          phase={composerPhase}
          profileView={intakeView}
          busy={intakeStarting || profileActionBusy}
          intakeError={intakeError}
          onConfirmProfile={() => {
            void actOnProfile('confirm');
          }}
          onProfileIncorrect={() => {
            void actOnProfile('correct');
          }}
          onRetrySources={resetProfile}
        />
        <ProcessSection />
        {productStoryEnabled ? <ProductStorySection /> : null}
        <WaitlistSection storeUrl={storeUrl} />
      </main>

      <LandingFooter />

      {authIntent !== null ? (
        <MerchantAuthModal
          intent={authIntent}
          close={() => {
            setAuthIntent(null);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * `/business-profile` is the one merchant route reachable before sign-in: the
 * intake preview a visitor confirms from the landing page. Signed in, it is
 * a workspace page like every other and composes inside the sidebar shell;
 * anonymous, it gets the public frame. The old horizontal customer header
 * no longer exists, so neither branch can render it.
 */
export function MerchantProfileRoute(): ReactNode {
  const { session } = useSession();
  const location = useLocation();
  // Wait for the session before choosing a surface, so the intake is read
  // once by whichever page owns it rather than twice across a remount.
  if (session.status === 'loading') return <LoadingPanel />;
  if (session.status === 'authenticated' && session.account.role === 'MERCHANT') {
    // One shell instance for the whole workspace: the merchant's profile is
    // a tabbed page of the layout, so the sidebar never remounts around it.
    return (
      <Navigate
        to={{ pathname: '/business-profile/business', search: location.search }}
        replace
      />
    );
  }
  return (
    <div className="mj-merchant-public-frame">
      <MerchantPublicHeader />
      <MerchantProfilePage />
    </div>
  );
}

/**
 * The server still hands out intake-scoped continuations (`/merchant/
 * intakes/:id/recommendations`). Inside the workspace every intake belongs
 * to one Production Line, so the continuation lands on that line's page.
 */
export function IntakeLineRedirect(): ReactNode {
  const { intakeId } = useParams<{ intakeId: string }>();
  const { refresh } = useWorkspace();
  const [fresh, setFresh] = useState<WorkspaceLists | null | undefined>(undefined);

  // The intake may be seconds old: re-read the lines before deciding.
  useEffect(() => {
    let live = true;
    void refresh().then((lists) => {
      if (live) setFresh(lists);
    });
    return () => {
      live = false;
    };
  }, [intakeId, refresh]);

  if (fresh === undefined) return <LoadingPanel />;
  const pending = fresh?.pending.find(
    (item) => (item.intakeId ?? item.brief?.intakeId) === intakeId,
  );
  const configured = fresh?.lines.find((item) => item.brief?.intakeId === intakeId);
  const lineId =
    pending?.productionLineId ?? configured?.productionLine.productionLineId;
  if (lineId !== undefined) {
    return <Navigate to={`/production-lines/${encodeURIComponent(lineId)}`} replace />;
  }
  return (
    <Navigate
      to={`/business-profile/business?intake=${encodeURIComponent(intakeId ?? '')}`}
      replace
    />
  );
}
